import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Divider,
  Link,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAppSelector } from 'src/app/reduxCustomHooks';
import { usageResetDate } from 'src/utils/usagePeriod';
import { useIsDemoUser } from 'src/components/hooks/useIsDemoUser';
import { useGetUsage } from 'src/components/hooks/useGetUsage';

type usageLineType = {
  icon: string;
  title: string;
  count?: number | string;
  loading?: boolean;
  tooltip?: ReactNode;
};

const UsageRow: FC<usageLineType> = (props) => {
  const { icon, title, count, loading, tooltip } = props;
  const value = (
    <Typography fontSize={32} fontWeight={600}>
      {loading ? <Skeleton width={60} /> : count}
    </Typography>
  );
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" alignItems="center" columnGap={2}>
        <img
          src={process.env.PUBLIC_URL + '/icons/svg/' + icon}
          width={32}
          height={32}
          alt="task usage icon"
        />
        <Typography fontSize={20}>{title}</Typography>
      </Stack>
      {tooltip && !loading ? (
        <Tooltip title={tooltip} arrow>
          <span>{value}</span>
        </Tooltip>
      ) : (
        value
      )}
    </Stack>
  );
};

type DashboardUsagePropsType = {};

export const DashboardUsage: FC<DashboardUsagePropsType> = () => {
  const { t } = useTranslation();

  const isManager = useAppSelector((state) => state.auth.isManager);

  const isDemoUser = useIsDemoUser();

  const { data, isLoading } = useGetUsage(isManager);

  const maxPages = data?.maxPages;
  const pageCount = data?.pageCount || 0;
  const pagePct =
    maxPages && maxPages > 0
      ? Math.min(100, Math.round((pageCount / maxPages) * 100))
      : null;

  // periodEnd is the last millisecond of the month; the reset is the next day.
  const resetLabel = usageResetDate(data?.periodEnd);
  const expiryLabel = data?.expirationTime
    ? new Date(data.expirationTime).toLocaleDateString()
    : null;

  const usageLinesArray = [
    {
      icon: 'archive-grey.svg',
      title: t('workspace'),
      count: data?.workspaceCount || 0,
    },
    {
      icon: 'files.svg',
      title: t('tasks'),
      count: data?.taskCount || 0,
    },
    {
      icon: 'page.svg',
      title: t('pagesThisMonth'),
      count:
        pagePct != null
          ? `${pagePct}%`
          : pageCount,
      tooltip:
        maxPages != null
          ? `${pageCount} / ${maxPages} ${t('pages')}`
          : undefined,
    },
  ];

  return (
    <Paper
      component={Stack}
      divider={
        <Divider
          orientation="horizontal"
          flexItem
          sx={{ borderColor: 'customGrey.light' }}
        />
      }
      rowGap={1}
      elevation={0}
      sx={{
        p: 3,
        width: '100%',
        border: (theme) => `1px solid ${theme.palette.hairline}`,
      }}
    >
      {usageLinesArray.map((item, index) => (
        <UsageRow key={index} {...item} loading={isLoading} />
      ))}
      {pagePct != null && (
        <Stack spacing={0.5} sx={{ pt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={pagePct}
            color={pagePct >= 100 ? 'error' : pagePct >= 80 ? 'warning' : 'primary'}
          />
          {resetLabel && (
            <Typography variant="body2" color="text.secondary">
              {t('usageResetsOn', { date: resetLabel })}
            </Typography>
          )}
          {expiryLabel && (
            <Typography variant="body2" color="text.secondary">
              {t('usageExpiresOn', { date: expiryLabel })}
            </Typography>
          )}
        </Stack>
      )}
      {isManager && !isDemoUser && (
        <Link component={RouterLink} to="/usage" variant="body2" sx={{ pt: 1 }}>
          {t('usageByMonth.title')}
        </Link>
      )}
    </Paper>
  );
};
